import { gramVariations, kgVariations, literVariations, milliliterVariations, unitVariations } from "../data/unitVariations";

export const normalizeUOM = (uom, kgVars, gVars, lVars, mlVars) => {
    const val = uom?.toLowerCase();

    if (kgVars.includes(val)) return 'kg';
    if (gVars.includes(val)) return 'g';
    if (lVars.includes(val)) return 'ltr';
    if (mlVars.includes(val)) return 'ml';

    return val;
};

export const convertToBaseQuantity = ({
    quantity,
    fromUom,
    toUom,
    itemUnit,
    kgVars,
    gVars,
    lVars,
    mlVars
}) => {
    let q = parseFloat(quantity);
    const unit = parseFloat(itemUnit) || 1;

    const from = normalizeUOM(fromUom, kgVars, gVars, lVars, mlVars);
    const to = normalizeUOM(toUom, kgVars, gVars, lVars, mlVars);

    if (to && from !== to) {
        if (from === 'kg' && to === 'g') q = q / 1000;
        else if (from === 'g' && to === 'kg') q = q * 1000;

        else if (from === 'ltr' && to === 'ml') q = q / 1000;
        else if (from === 'ml' && to === 'ltr') q = q * 1000;

        return q / unit;
    } else {
        return q / unit;
    }

};

export const convertToNewBaseQuantity = ({
    quantity,
    fromUom,
    toUom,
    kgVars,
    gVars,
    lVars,
    mlVars
}) => {
    let q = parseFloat(quantity);

    const from = normalizeUOM(fromUom, kgVars, gVars, lVars, mlVars);
    const to = normalizeUOM(toUom, kgVars, gVars, lVars, mlVars);

    if (!to || from === to) return q;

    if (from === 'kg' && to === 'g') return q * 1000;
    if (from === 'g' && to === 'kg') return q / 1000;

    if (from === 'ltr' && to === 'ml') return q * 1000;
    if (from === 'ml' && to === 'ltr') return q / 1000;

    return q;
};

export const roundOffPrecicion = (qty) => {
    const num = Number(qty);

    if (!Number.isFinite(num)) return 0;

    const decimal = Math.abs(num % 1);

    if (decimal <= 0.03 || decimal >= 0.97) {
        return Math.round(num);
    }

    return Number(num.toFixed(4));
};

export const convertToDisplayUnit = (quantity, uom, new_uom, unit) => {
    let qty = parseFloat(quantity);

    if (isNaN(qty)) return 0;

    const normalizedUom = normalizeUomUnit(uom);
    const normalizedNewUom = normalizeUomUnit(new_uom);

    qty = qty * Number(unit || 1);

    if (!new_uom || normalizedUom === normalizedNewUom) {
        return roundOffPrecicion(qty);
    }

    if (normalizedUom === "kg" && normalizedNewUom === "gram") {
        qty = qty * 1000;
    } else if (normalizedUom === "gram" && normalizedNewUom === "kg") {
        qty = qty / 1000;
    } else if (normalizedUom === "liter" && normalizedNewUom === "milliliter") {
        qty = qty * 1000;
    } else if (normalizedUom === "milliliter" && normalizedNewUom === "liter") {
        qty = qty / 1000;
    }

    return roundOffPrecicion(qty);
};

export const normalizeUomUnit = (uom) => {
    if (!uom) return uom;
    const lower = uom.toLowerCase();
    for (const [canonical, variations] of Object.entries(unitVariations)) {
        if (variations.includes(lower)) return canonical;
    }
    return lower;
};

export const formatNumber = (val) => {
    if (val === null || val === undefined) return '0';

    const parsed = parseFloat(val);
    return parsed.toString();
};

const UNIT_LABELS = {
    ...Object.fromEntries(kgVariations.map((u) => [u.toLowerCase(), "kg"])),
    ...Object.fromEntries(gramVariations.map((u) => [u.toLowerCase(), "gms"])),
    ...Object.fromEntries(literVariations.map((u) => [u.toLowerCase(), "ltr"])),
    ...Object.fromEntries(milliliterVariations.map((u) => [u.toLowerCase(), "ml"])),
    can: "can", cans: "can", tin: "can", tins: "can",
    box: "box", boxes: "box",
    btl: "btl", bottle: "btl", bottles: "btl",
    bag: "bag", bags: "bag",
    pkt: "pkt", packet: "pkt", packets: "pkt",
    carton: "ctn", ctn: "ctn", cartons: "ctn",
    dozen: "doz", doz: "doz",
    unit: "unit", units: "unit",
    pcs: "pcs", piece: "pcs", pieces: "pcs",
    nos: "nos", no: "nos",
    bunch: "bunch", bunches: "bunch",
    roll: "roll", rolls: "roll",
};

const getLabel = (uomString) => {
    if (!uomString) return "";
    return UNIT_LABELS[uomString.trim().toLowerCase()] ?? uomString.trim();
};

const getConversionFactor = (fromUom, toUom) => {
    if (!fromUom || !toUom) return 1;
    const from = fromUom.trim().toLowerCase();
    const to = toUom.trim().toLowerCase();
    if (from === to) return 1;
    if (kgVariations.includes(from) && gramVariations.includes(to)) return 1000;
    if (gramVariations.includes(from) && kgVariations.includes(to)) return 0.001;
    if (literVariations.includes(from) && milliliterVariations.includes(to)) return 1000;
    if (milliliterVariations.includes(from) && literVariations.includes(to)) return 0.001;
    return 1;
};

export const formatQuantity = (
    item,
    quantity,
    hasUnit = true,
    itemName,
    stockItems = []
) => {
    if (quantity === null || quantity === undefined || isNaN(quantity)) return "0";

    const parsedQty = parseFloat(quantity);
    if (parsedQty === 0) return "0";

    const qty = Math.abs(parsedQty);
    const sign = parsedQty < 0 ? "-" : "";

    let resolvedItem = item;
    let resolvedUnitDefinition = item?.unitdefinition;

    if (!hasUnit) {
        const matchedStockItem = stockItems.find(
            (stockItem) => stockItem?.ItemName === itemName
        );

        if (matchedStockItem) {
            resolvedItem = matchedStockItem;
            resolvedUnitDefinition = matchedStockItem?.unit_definitions?.[0];
        }
    }

    const primaryLabel = getLabel(resolvedItem?.primary_unit);
    const secondaryLabel = getLabel(resolvedItem?.secondary_unit);

    const unitSize = parseFloat(resolvedUnitDefinition?.unit);
    const baseUom = resolvedUnitDefinition?.uom?.trim().toLowerCase();

    if (!unitSize || !baseUom) {
        return `${sign}${roundOffPrecicion(qty)} ${primaryLabel}`;
    }

    const primaryUom = resolvedItem?.primary_unit?.trim().toLowerCase();
    const secondaryUom = resolvedItem?.secondary_unit?.trim().toLowerCase();
    const primaryMultiplier = primaryUom === baseUom ? 1 : unitSize;
    const totalInBaseUnit = qty * unitSize;

    const hasSecondary =
        !!resolvedItem?.secondary_unit &&
        resolvedItem.secondary_unit.toLowerCase() !==
        resolvedItem.primary_unit?.toLowerCase();

    if (!hasSecondary) {
        const primaryQty = totalInBaseUnit / primaryMultiplier;
        return `${sign}${roundOffPrecicion(primaryQty)} ${primaryLabel}`;
    }

    let secondaryMultiplier;
    if (secondaryUom === baseUom) {
        secondaryMultiplier = 1;
    } else {
        secondaryMultiplier =
            getConversionFactor(baseUom, secondaryUom) === 1
                ? 1
                : 1 / getConversionFactor(baseUom, secondaryUom);
    }

    const primaryQty = Math.floor(totalInBaseUnit / primaryMultiplier);
    const remainder = totalInBaseUnit - primaryQty * primaryMultiplier;
    const secondaryQty = remainder / secondaryMultiplier;

    if (primaryQty === 0) return `${sign}${roundOffPrecicion(secondaryQty)} ${secondaryLabel}`;
    if (secondaryQty === 0) return `${sign}${primaryQty} ${primaryLabel}`;

    return (
        <div>
            {sign}{primaryQty} {primaryLabel} +{" "}
            {roundOffPrecicion(secondaryQty)} {secondaryLabel}
        </div>
    );
};

export const getUnitInfo = (itemName, stockItems = []) => {
    const matched = (stockItems || []).find(
        (stockItem) => stockItem.ItemName === itemName
    );

    if (!matched) {
        return { primary_unit: null, secondary_unit: null, unitdefinition: null };
    }

    return {
        primary_unit: matched.primary_unit,
        secondary_unit: matched.secondary_unit,
        unitdefinition: matched.unit_definitions?.[0] || null,
    };
};

export const shouldShowSecondaryUnit = (unitInfo) => {
    const primaryUnit = (unitInfo?.primary_unit || "").toLowerCase().trim();
    const secondaryUnit = (unitInfo?.secondary_unit || "").toLowerCase().trim();
    const defUom = (unitInfo?.unitdefinition?.uom || "").toLowerCase().trim();

    if (!defUom) {
        return primaryUnit !== secondaryUnit;
    }

    return !(primaryUnit === secondaryUnit && secondaryUnit === defUom);
};

export const computeQuantityFromPrimarySecondary = (
    primaryQty,
    secondaryQty,
    unitInfo
) => {
    const unitSize = parseFloat(unitInfo?.unitdefinition?.unit) || 1;
    const baseUom = (unitInfo?.unitdefinition?.uom || "").trim().toLowerCase();
    const primaryUom = (unitInfo?.primary_unit || "").trim().toLowerCase();
    const secondaryUom = (unitInfo?.secondary_unit || "").trim().toLowerCase();

    const p = parseFloat(primaryQty) || 0;
    const s = parseFloat(secondaryQty) || 0;

    const primaryInBaseUnit = primaryUom === baseUom ? p : p * unitSize;

    let secondaryInBaseUnit;
    if (secondaryUom === baseUom) {
        secondaryInBaseUnit = s;
    } else {
        const factor = getConversionFactor(baseUom, secondaryUom);
        secondaryInBaseUnit = factor === 1 ? s : s * (1 / factor);
    }

    const totalInBaseUnit = primaryInBaseUnit + secondaryInBaseUnit;
    return unitSize ? totalInBaseUnit / unitSize : totalInBaseUnit;
};

export const splitQuantityToPrimarySecondary = (quantity, unitInfo) => {
    const qty = parseFloat(quantity) || 0;
    if (!qty) return { primaryQty: "", secondaryQty: "" };

    const unitSize = parseFloat(unitInfo?.unitdefinition?.unit) || 1;
    const baseUom = (unitInfo?.unitdefinition?.uom || "").trim().toLowerCase();
    const primaryUom = (unitInfo?.primary_unit || "").trim().toLowerCase();
    const secondaryUom = (unitInfo?.secondary_unit || "").trim().toLowerCase();

    const primaryMultiplier = primaryUom === baseUom ? 1 : unitSize;
    const totalInBaseUnit = qty * unitSize;

    if (!shouldShowSecondaryUnit(unitInfo)) {
        const primaryQty = totalInBaseUnit / primaryMultiplier;
        return {
            primaryQty: primaryQty ? String(parseFloat(primaryQty.toFixed(4))) : "",
            secondaryQty: "",
        };
    }

    let secondaryMultiplier;
    if (secondaryUom === baseUom) {
        secondaryMultiplier = 1;
    } else {
        const factor = getConversionFactor(baseUom, secondaryUom);
        secondaryMultiplier = factor === 1 ? 1 : 1 / factor;
    }

    const primaryQty = Math.floor(totalInBaseUnit / primaryMultiplier);
    const remainder = totalInBaseUnit - primaryQty * primaryMultiplier;
    const secondaryQty = remainder / secondaryMultiplier;

    return {
        primaryQty: primaryQty ? String(primaryQty) : "",
        secondaryQty: secondaryQty ? String(parseFloat(secondaryQty.toFixed(4))) : "",
    };
};